import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { PowerType } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import i18next from 'i18next'

import { convertAreaLabel } from '~/models/area'
import { licenseTypeLabel } from '~/models/licenseType'
import type { BgMasterRow } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import type { CsvColumnDef } from '~/pages/AdminOcctoMaster/tabs/_common/models/csvExport'

const NO_VALUE = '—'

// powerTypeLabel は ~/models/area/index.ts の convertAreaLabel と同じ pattern
// (= i18next.t.bind(i18next) で純関数 / module-level) で実装する。
// licenseTypeLabel は ~/models/licenseType.ts に共通化済み (= BG / 提出者 / 取引先で共有)。

export function powerTypeLabel(value: PowerType | undefined): string {
  if (value === undefined) {
    return NO_VALUE
  }
  const t = i18next.t.bind(i18next)
  switch (value) {
    case PowerType.NON_ADJUSTABLE:
      return t('page:adminOcctoMaster.bgMaster.powerType.nonAdjustable')
    case PowerType.ADJUSTABLE:
      return t('page:adminOcctoMaster.bgMaster.powerType.adjustable')
    case PowerType.FIT_EXCEPTION_1:
      return t('page:adminOcctoMaster.bgMaster.powerType.fitException1')
    case PowerType.FIT_EXCEPTION_2:
      return t('page:adminOcctoMaster.bgMaster.powerType.fitException2')
    case PowerType.FIT_EXCEPTION_3:
      return t('page:adminOcctoMaster.bgMaster.powerType.fitException3')
    case PowerType.FIP:
      return t('page:adminOcctoMaster.bgMaster.powerType.fip')
    case PowerType.UNSPECIFIED:
      return NO_VALUE
    default:
      return NO_VALUE
  }
}

export function isSelfWheeling(value: LicenseType): boolean {
  return value === LicenseType.SELF_WHEELING
}

/**
 * BG マスタタブの CSV エクスポート カラム定義
 * (spec AC-012b / design doc §5「タブ別 CSV カラム」)。
 * List レスポンスのフィールドのみを対象とし (AC-04)、詳細モーダルでのみ
 * 見える構成事業者リスト等は含まない。 enum / AreaCode は既存の label
 * 変換関数を通し、コード値でなく表示名を出力する。
 */
export function bgMasterCsvColumns(): Array<CsvColumnDef<BgMasterRow>> {
  return [
    {
      headerKey: 'page:adminOcctoMaster.bgMaster.column.bgCode',
      accessor: (row) => row.bgCode,
    },
    {
      headerKey: 'page:adminOcctoMaster.bgMaster.column.areaCode',
      accessor: (row) => convertAreaLabel(row.areaCode),
    },
    {
      headerKey: 'page:adminOcctoMaster.bgMaster.column.licenseType',
      accessor: (row) => licenseTypeLabel(row.licenseType),
    },
    {
      headerKey: 'page:adminOcctoMaster.bgMaster.column.bgName',
      accessor: (row) => row.bgName,
    },
    {
      headerKey: 'page:adminOcctoMaster.bgMaster.column.powerType',
      accessor: (row) => powerTypeLabel(row.powerType),
    },
    {
      headerKey: 'page:adminOcctoMaster.bgMaster.column.memberCount',
      accessor: (row) => String(row.memberCount),
    },
    {
      headerKey: 'page:adminOcctoMaster.bgMaster.column.plantCount',
      // 発電 BG だけ数える (発電以外は「—」)。画面の列と同じ。
      accessor: (row) =>
        row.licenseType === LicenseType.GENERATION
          ? String(row.plantCount)
          : NO_VALUE,
    },
  ]
}
